import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, styled } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import { normalizeFileName } from "../../../../../../common/utils/utils";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { GEDDocumento } from "../../../../../../common/domain/models/ged_documento";
import { zodResolver } from "@hookform/resolvers/zod";

import DefaultFormInput from "../../../../../../common/components/default_form_input";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { GedDocumentoForm, GedDocumentoSchema } from "../../../schemas/GED_terceirizacao/ged_documento";

/**
 * VisuallyHiddenInput
 */

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

/**
 * CopyDocumentoModal
 */

type Props = {
  open: boolean;
  saving: boolean;
  selectedDocumento: GEDDocumento | null;
  onClose: () => void;
  onSubmit: (form: GedDocumentoForm) => void;
  onDownloadFile: () => void;
  onDownloadFilePdf: () => void;
};

export const CopyDocumentoModal = ({
  open,
  saving,
  selectedDocumento,
  onClose,
  onSubmit,
  onDownloadFile,
  onDownloadFilePdf,
}: Props) => {
  const hookForm = useForm<GedDocumentoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(GedDocumentoSchema),
  });

  const [gedDocumento, setGedDocumento] = useState<GEDDocumento | null>(null);

  useEffect(() => {
    if (open) {
      if (selectedDocumento) {
        hookForm.setValue('arquivo', selectedDocumento.descricao ?? '');
        hookForm.setValue('observacoes', selectedDocumento.observacao ?? '');
        hookForm.setValue('file', null);
        hookForm.setValue('filePdf', null);
        hookForm.setValue('limparArquivo', false);
        hookForm.setValue('limparArquivoPdf', false);

        setGedDocumento(({ ...selectedDocumento, nome: null, nomePdf: null }));
      }
    }
  }, [
    open,
    selectedDocumento,
    hookForm,
  ]);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [
    saving,
    onClose,
  ]);

  const handleSubmit = useCallback((form: any) => {
    if (!saving) onSubmit(form);
  }, [
    saving,
    onSubmit,
  ]);

  const handleDownloadFile = useCallback(() => {
    if (!saving) onDownloadFile();
  }, [
    saving,
    onDownloadFile,
  ]);

  const handleDownloadPdf = useCallback(() => {
    if (!saving) onDownloadFilePdf();
  }, [
    saving,
    onDownloadFilePdf,
  ]);

  const handleDeleteFile = useCallback(() => {
    hookForm.setValue('limparArquivo', true);
    setGedDocumento(doc => {
      if (doc === null) return doc;
      return ({ ...doc, nome: null, arquivo: null });
    });
  }, [
    hookForm,
  ]);

  const handleDeletePdf = useCallback(() => {
    if (window.confirm('Você deseja excluir este item?')) {
      hookForm.setValue('limparArquivoPdf', true);
      setGedDocumento(doc => {
        if (doc === null) return doc;
        return ({ ...doc, nomePdf: null, arquivoPdf: null });
      });
    }
  }, [
    hookForm,
  ]);

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>, onChange: (e: any) => void) => {
    if (!saving && e.target.files && e.target.files.length > 0) {
      const f = e.target.files.item(0);
      if (f) {
        hookForm.setValue('limparArquivo', false);
        hookForm.setValue('file', f);



        onChange(f);
      }
    }
  }, [
    saving,
    hookForm,
  ]);

  const handlePdfChange = useCallback((e: ChangeEvent<HTMLInputElement>, onChange: (e: any) => void) => {
    if (!saving && e.target.files && e.target.files.length > 0) {
      const f = e.target.files.item(0);
      if (f) {
        hookForm.setValue('limparArquivoPdf', false);
        hookForm.setValue('filePdf', f);



        onChange(f);
      }
    }
  }, [
    saving,
    hookForm,
  ]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(handleSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "40rem",
          },
        },
      }}>
      <DialogTitle>
        Copiar documento
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer title={"Informações"} arrangement="fullRow">
            <>
              <DefaultFormInput
                name={'arquivo'}
                label={"Arquivo"}
                readOnly={saving}
                required={true}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={'observacoes'}
                label={"Observações"}
                readOnly={saving}
                multiline={true}
                rows={10}
                reactHookForm={hookForm}
              />

              <div className="flex w-full">
                <Controller
                  name='file'
                  control={hookForm.control}
                  render={({ field }) => (gedDocumento && gedDocumento.nome && gedDocumento.nome.length > 0) ? (
                    <div className="w-full flex flex-row justify-between items-center">
                      <span
                        className="cursor-pointer"
                        onMouseDown={handleDownloadFile}>
                        {gedDocumento && gedDocumento.nome && gedDocumento.nome.length > 40
                          ? `${gedDocumento.nome.substring(0, 40)}...`
                          : gedDocumento.nome}
                      </span>
                      <IconButton onClick={handleDeleteFile}>
                        <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
                      </IconButton>
                    </div>
                  ) : (
                    <PrimaryButton
                      component="label"
                      role={undefined}
                      variant="contained"
                      tabIndex={-1}
                      disabled={saving}
                      startIcon={<DynamicIcon nameIcon="Download" size={25} className="text-white" />}>
                      {field.value ? normalizeFileName(field.value?.name, 40) : "Escolher arquivo"}
                      <VisuallyHiddenInput
                        type="file"
                        onChange={(e) => handleFileChange(e, field.onChange)}
                        multiple={false} />
                    </PrimaryButton>
                  )} />
              </div>

              <div className="flex w-full">
                <Controller
                  name='filePdf'
                  control={hookForm.control}
                  render={({ field }) => (gedDocumento && gedDocumento.nomePdf && gedDocumento.nomePdf.length > 0) ? (
                    <div className="w-full flex flex-row justify-between items-center">
                      <span
                        className="cursor-pointer"
                        onMouseDown={handleDownloadPdf}>
                        {gedDocumento && gedDocumento.nomePdf && gedDocumento.nomePdf.length > 40
                          ? `${gedDocumento.nomePdf.substring(0, 40)}...`
                          : gedDocumento.nomePdf}
                      </span>
                      <IconButton onClick={handleDeletePdf}>
                        <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
                      </IconButton>
                    </div>
                  ) : (
                    <PrimaryButton
                      component="label"
                      role={undefined}
                      variant="contained"
                      tabIndex={-1}
                      disabled={saving}
                      startIcon={<DynamicIcon nameIcon="Download" size={25} className="text-white" />}>
                      {field.value ? normalizeFileName(field.value?.name, 40) : "Escolher arquivo PDF"}
                      <VisuallyHiddenInput
                        type="file"
                        accept=".pdf"
                        onChange={(e) => handlePdfChange(e, field.onChange)}
                        multiple={false} />
                    </PrimaryButton>
                  )} />
              </div>
            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div></div>

          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}